import React from 'react';
import { FormProvider } from 'react-hook-form';
import { Button, Input, Label, Spinner } from '@/Components/shadcn';
import { useUserContext } from '@/Context/UserContext';
import UIStrings from '@/Utils/UIStrings';
import EmailCandidateEditor from './EmailCandidateEditor/EmailCandidateEditor';
import { useEmailCandidateForm } from './useEmailCandidateForm';
import { useEmailIntegrationStatus } from '../../../../../../AccountSettings/AccountSettingsTabs/IntegrationsTab/api/useOAuthEmailIntegration';
import { RHFInput } from '@/Components/shadcn/rhf-input';
import TMWingsAnimation from '@/Components/Animations/TMWingsAnimation/TMWingsAnimation';
import TypingTextAnimation from '@/Components/Animations/TypingTextAnimation';

interface EmailCandidateFormProps {
    onSubmit: Parameters<ReturnType<typeof useEmailCandidateForm>['handleSubmit']>['0'];
}

const EmailCandidateForm = ({ onSubmit }: EmailCandidateFormProps) => {
    const form = useEmailCandidateForm();
    const { user } = useUserContext();
    const { data: integration } = useEmailIntegrationStatus();

    const handleSubmit = form.handleSubmit(onSubmit);

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <Label className="capitalize">{UIStrings.From}</Label>
                    <Input
                        readOnly
                        disabled
                        value={`${user.firstName} ${user.lastName} <${integration?.emailAddress || ''}>`}
                        className="bg-muted"
                    />
                </div>
                <div className="flex flex-col gap-2">
                    <Label htmlFor="subject">{UIStrings.Subject}</Label>
                    {form.formState.isLoading ? (
                        <div className="border-input bg-background flex h-10 w-full items-center gap-1 rounded-xl border p-2">
                            <TMWingsAnimation width="28px" height="28px" />
                            <TypingTextAnimation deletionSpeed={20} texts={[UIStrings.GeneratingSubjectWithAI]} />
                        </div>
                    ) : (
                        <RHFInput className="w-full" placeholder={UIStrings.AddSubject} {...form.register('subject')} />
                    )}
                </div>
                <EmailCandidateEditor />
                <div className="flex justify-end gap-2">
                    <Button
                        type="submit"
                        size="lg"
                        disabled={form.formState.disabled || form.formState.isLoading || form.formState.isSubmitting}
                    >
                        {form.formState.isSubmitting && <Spinner className="size-4" />}
                        {UIStrings.SendEmail}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default EmailCandidateForm;
